import React, { memo, useMemo, useRef, useEffect, useState, useCallback } from 'react';
import { Row, Col, Spin, theme, List, Image, Button, Tooltip } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import VirtualList from 'rc-virtual-list';
import MessageBubble from './MessageBubble/MessageBubble';
import { resolveMediaDisplayType } from './MessageBubble/MessageMediaGallery/MessageMediaGallery';
import { useTheme } from '../../../../Shared/GlobalContext/ThemeContext';

const MessageList = memo(({
    messages,
    loading,
    messageRefs,
    handleReplyClick,
    onReplyAction,
    scrollApiRef,
    onJumpToLatest,
    setSelectedMessage,
    setDrawerVisible,
    onDeleteMessage,
    invertMessageSides = false,
    screens,
    onScroll,
    bottomInset = 0,
    hasMore = false,
    hasMoreNewer = false,
    conversationId,
    TypingIndicator,
    CountdownIndicator,
    isFetchingMoreTop = false,
    isFetchingMoreBottom = false,
    highlightedMessageId
}) => {
    const { isDarkMode } = useTheme();
    const { token } = theme.useToken();
    const { t } = useTranslation();

    const loadingSpinnerColor = isDarkMode ? token.colorTextLightSolid : token.colorText;
    const [isImagePreviewOpen, setIsImagePreviewOpen] = useState(false);
    const [previewImageIndex, setPreviewImageIndex] = useState(0);

    // The gallery includes every image currently loaded in this conversation.
    const conversationImages = useMemo(() => (messages || []).flatMap((message) => (
        (message?.medias || [])
            .filter((media) => resolveMediaDisplayType(media) === 'image' && media?.url)
            .map((media) => media.url)
    )), [messages]);

    const openImagePreview = useCallback((url) => {
        const index = conversationImages.indexOf(url);
        setPreviewImageIndex(index >= 0 ? index : 0);
        setIsImagePreviewOpen(true);
    }, [conversationImages]);

    // Order items: reverse real messages, then append temporary items at the end (bottom of visual list)
    const orderedMessages = useMemo(() => {
        const temporaryItems = (messages || []).filter(item => item?.isTemporary);
        const realItems = (messages || []).filter(item => !item?.isTemporary);
        // Messages are reversed for display (oldest first in DOM, newest last).
        // Temporary items (typing indicator) go LAST so they appear at the BOTTOM visually.
        return [...realItems.reverse(), ...temporaryItems];
    }, [messages]);

    const scrollContainerRef = useRef(null);
    const innerContentRef = useRef(null);
    const nearBottomRef = useRef(true);
    const shouldScrollToBottomRef = useRef(true);
    const userHasScrolledRef = useRef(false);
    const [showJumpToLatest, setShowJumpToLatest] = useState(false);

    const jumpToLatest = useCallback(() => {
        if (onJumpToLatest) {
            setShowJumpToLatest(false);
            onJumpToLatest();
            return;
        }

        const el = scrollContainerRef.current;
        if (!el) return;

        el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
        nearBottomRef.current = true;
        setShowJumpToLatest(false);
    }, [onJumpToLatest]);

    // Expose an imperative API to parent to control scrolling
    useEffect(() => {
        if (!scrollApiRef || typeof scrollApiRef !== 'object') return;
        scrollApiRef.current = {
            scrollToBottom: () => {
                const el = scrollContainerRef.current;
                if (!el) return;
                requestAnimationFrame(() => {
                    el.scrollTop = el.scrollHeight;
                    nearBottomRef.current = true;
                    setShowJumpToLatest(false);
                });
            }
        };
        return () => { if (scrollApiRef) scrollApiRef.current = null; };
    }, [scrollApiRef]);

    // Callback ref for tracking last message (not used for scrolling)
    const lastMessageRef = useCallback((node) => {
        // Just for tracking, actual scrolling done via effect
    }, []);

    // Initial scroll to bottom when messages first load
    const hasScrolledInitiallyRef = useRef(false);
    useEffect(() => {
        if (loading || hasScrolledInitiallyRef.current) return;
        if (orderedMessages.length === 0) return;

        // First time messages load
        shouldScrollToBottomRef.current = true;
        hasScrolledInitiallyRef.current = true;
    }, [loading, orderedMessages.length]);

    // Reset scroll flag when conversation changes
    useEffect(() => {
        shouldScrollToBottomRef.current = true;
        nearBottomRef.current = true;
        userHasScrolledRef.current = false;
        setShowJumpToLatest(false);
        hasScrolledInitiallyRef.current = false; // Reset for new conversation
    }, [conversationId]);

    // Unified scroll-to-bottom logic
    const prevFetchingBottomRef = useRef(false);

    // Loading a newer page from the bottom should preserve the message the
    // user was reading. Prevent the resize observer from treating that state
    // as permission to auto-scroll to the newly extended bottom.
    useEffect(() => {
        if (isFetchingMoreBottom) {
            nearBottomRef.current = false;
        }
    }, [isFetchingMoreBottom]);

    useEffect(() => {
        // Track previous fetch state to detect completion
        const justFinishedFetchingBottom = prevFetchingBottomRef.current && !isFetchingMoreBottom;
        prevFetchingBottomRef.current = isFetchingMoreBottom;

        // Don't scroll if we're in the middle of loading older messages
        if (isLoadingOlderMessagesRef.current) return;

        // If we just finished bottom fetching, trust that the parent component restored scroll position
        if (justFinishedFetchingBottom) return;

        const el = scrollContainerRef.current;
        if (!el) return;

        // Determine if we should scroll
        // 1. Explicit flag set (e.g. initial load or conversation switch)
        // 2. Auto-scroll: User was near bottom AND we are not loading top OR bottom history
        const shouldExplicitScroll = shouldScrollToBottomRef.current;
        const shouldAutoScroll = !isFetchingMoreTop && !isFetchingMoreBottom && nearBottomRef.current && orderedMessages.length > 0;

        if ((shouldExplicitScroll && !userHasScrolledRef.current) || shouldAutoScroll) {
            // Consume an explicit initial-load request immediately. Waiting until
            // the animation frame lets multiple ResizeObserver callbacks queue
            // a stale scroll-to-bottom after the user has started scrolling up.
            shouldScrollToBottomRef.current = false;
            // Use requestAnimationFrame to ensure DOM has updated with new messages
            requestAnimationFrame(() => {
                if (el) { // Check ref again in callback
                    el.scrollTop = el.scrollHeight;
                }
            });
        }
    }, [orderedMessages.length, conversationId, isFetchingMoreTop, isFetchingMoreBottom]);

    // Make sure props used in ResizeObserver are up to date
    const fetchStateRef = useRef({ 
        isFetchingMoreTop, 
        isFetchingMoreBottom, 
        orderedMessagesLength: orderedMessages.length 
    });
    
    useEffect(() => {
        fetchStateRef.current = { 
            isFetchingMoreTop, 
            isFetchingMoreBottom, 
            orderedMessagesLength: orderedMessages.length 
        };
    }, [isFetchingMoreTop, isFetchingMoreBottom, orderedMessages.length]);

    // Robust observer to catch asynchronous media loading and internal layout thrashing
    useEffect(() => {
        const scrollEl = scrollContainerRef.current;
        const innerEl = innerContentRef.current;

        if (!scrollEl || !innerEl) return;

        const resizeObserver = new ResizeObserver(() => {
            const state = fetchStateRef.current;
            const justFinishedFetchingBottom = prevFetchingBottomRef.current && !state.isFetchingMoreBottom;
            
            // Do not intervene if manual fetch is ongoing or just finished
            if (isLoadingOlderMessagesRef.current || justFinishedFetchingBottom) return;

            const shouldExplicitScroll = shouldScrollToBottomRef.current;
            const shouldAutoScroll = !state.isFetchingMoreTop && !state.isFetchingMoreBottom && nearBottomRef.current && state.orderedMessagesLength > 0;

            if ((shouldExplicitScroll && !userHasScrolledRef.current) || shouldAutoScroll) {
                shouldScrollToBottomRef.current = false;
                requestAnimationFrame(() => {
                    if (scrollContainerRef.current) {
                        scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
                    }
                });
            }
        });

        resizeObserver.observe(innerEl);

        return () => resizeObserver.disconnect();
    }, []);

    const pendingTopLoadRef = useRef(false);
    const [topLoading, setTopLoading] = useState(false);
    const anchorMessageIdRef = useRef(null);
    const anchorOffsetRef = useRef(0);
    const isLoadingOlderMessagesRef = useRef(false);
    const wasFetchingMoreTopRef = useRef(false);
    const handleScrollInternal = (e) => {
        const el = e.currentTarget;

        // A user scroll takes precedence over any delayed initial-load scroll.
        // Without this, a ResizeObserver callback can send the user back to the
        // bottom just after they first scroll up through a conversation.
        userHasScrolledRef.current = true;
        shouldScrollToBottomRef.current = false;

        const distanceFromBottom = el.scrollHeight - (el.scrollTop + el.clientHeight);
        // Consider within 60px from bottom as "near bottom"
        nearBottomRef.current = distanceFromBottom <= 60;
        setShowJumpToLatest(distanceFromBottom > 120);

        // Newer messages are appended after the visible items. Stop the
        // resize observer from pulling the user to that new bottom while the
        // next page is being requested.
        if (distanceFromBottom <= 60 && hasMoreNewer) {
            nearBottomRef.current = false;
        }

        // If scrolled to absolute top, prepare anchor for preserving position
        if (el.scrollTop === 0 && hasMore && !pendingTopLoadRef.current) {
            const items = el.querySelectorAll('.message-item');
            let anchorEl = null;
            for (let i = 0; i < items.length; i += 1) {
                const item = items[i];
                if (item.offsetTop >= el.scrollTop) {
                    anchorEl = item;
                    break;
                }
            }
            if (!anchorEl && items.length > 0) anchorEl = items[0];
            if (anchorEl) {
                anchorMessageIdRef.current = anchorEl.getAttribute('data-message-id');
                anchorOffsetRef.current = anchorEl.offsetTop - el.scrollTop;
                pendingTopLoadRef.current = true;
                isLoadingOlderMessagesRef.current = true; // Mark that we're loading older messages
                if (hasMore) {
                    setTopLoading(true);
                }
            }
        }

        if (typeof onScroll === 'function') {
            onScroll(e);
        }
    };

    // Restore the anchor only after the top-page request finishes. Depending on
    // list length alone leaves the loading state stuck for empty responses.
    useEffect(() => {
        const justFinishedFetchingTop = wasFetchingMoreTopRef.current && !isFetchingMoreTop;
        wasFetchingMoreTopRef.current = isFetchingMoreTop;

        if (!pendingTopLoadRef.current) return;
        if (!justFinishedFetchingTop) return;
        const el = scrollContainerRef.current;
        if (!el) {
            setTopLoading(false);
            isLoadingOlderMessagesRef.current = false;
            pendingTopLoadRef.current = false;
            return;
        }
        const anchorId = anchorMessageIdRef.current;
        if (!anchorId) {
            setTopLoading(false);
            isLoadingOlderMessagesRef.current = false;
            pendingTopLoadRef.current = false;
            return;
        }
        const anchorEl = el.querySelector(`.message-item[data-message-id="${anchorId}"]`);
        if (anchorEl) {
            requestAnimationFrame(() => {
                el.scrollTop = anchorEl.offsetTop - anchorOffsetRef.current;
                // Hide the top loader after restoring position
                setTopLoading(false);
                // Clear the loading flag AFTER position is restored
                isLoadingOlderMessagesRef.current = false;
            });
        } else {
            // If anchor not found, still clear the flag
            setTopLoading(false);
            isLoadingOlderMessagesRef.current = false;
        }
        pendingTopLoadRef.current = false;
        anchorMessageIdRef.current = null;
        anchorOffsetRef.current = 0;
    }, [isFetchingMoreTop, orderedMessages.length]);

    return (
        <div style={{ height: '100%', width: '100%', position: 'relative' }}>
            <div ref={scrollContainerRef} style={{ height: '100%', overflowX: 'hidden', overflowY: 'auto', padding: screens?.xs ? 8 : 16, paddingBottom: bottomInset }} onScroll={handleScrollInternal}>
                {topLoading && hasMore && (
                    <Row justify="center" style={{ padding: 0 }}>
                        <LoadingSpinner color={loadingSpinnerColor} />
                    </Row>
                )}
                <div 
                    ref={innerContentRef}
                    style={{
                    minHeight: '100%',
                    display: 'grid',
                    gridTemplateRows: '1fr auto',
                    gridTemplateAreas: '"spacer" "content"'
                }}>
                    <div style={{ gridArea: 'spacer' }}></div>
                    <div style={{ gridArea: 'content' }}>
                        <List>
                            <VirtualList
                                data={orderedMessages}
                                style={{
                                    height: 'auto',
                                    // Ensure only the outer container scrolls
                                    overflow: 'visible',
                                    padding: '0',
                                }}
                                itemHeight={80}
                                itemKey="id"
                                onScroll={onScroll}
                            >
                                {(item, index) => {
                                    const isLastItem = index === orderedMessages.length - 1;

                                    // Handle temporary indicator items
                                    if (item.isTemporary) {
                                        return (
                                            <div
                                                key={item.id}
                                                ref={isLastItem ? lastMessageRef : null}
                                                className="temporary-item"
                                                style={{ padding: '8px 0' }}
                                            >
                                                {item.type === 'typing-indicator' && TypingIndicator && (
                                                    <TypingIndicator isDarkMode={item.isDarkMode} />
                                                )}
                                                {item.type === 'countdown-indicator' && CountdownIndicator && (
                                                    <CountdownIndicator
                                                        isDarkMode={item.isDarkMode}
                                                        seconds={item.seconds}
                                                    />
                                                )}
                                            </div>
                                        );
                                    }

                                    // Handle regular message items
                                    const isHighlighted = item.id === highlightedMessageId;

                                    return (
                                        <div
                                            key={item.id}
                                            ref={isLastItem ? lastMessageRef : null}
                                            className={`message-item ${isHighlighted ? 'message-highlight-animation' : ''}`}
                                            data-message-id={item.id}
                                            style={{ padding: '8px 0' }}
                                        >
                                            <MessageBubble
                                                message={item}
                                                onReplyClick={handleReplyClick}
                                                onReplyAction={onReplyAction}
                                                messageRefs={messageRefs}
                                onMessageSelect={(msg) => {
                                    setSelectedMessage(msg);
                                    setDrawerVisible(true);
                                }}
                                onDeleteMessage={onDeleteMessage}
                                invertMessageSides={invertMessageSides}
                                                screens={screens}
                                                onImagePreview={openImagePreview}
                                            />
                                        </div>
                                    );
                                }}
                            </VirtualList>
                        </List>
                    </div>
                </div>
            </div>
            {isFetchingMoreBottom && (
                <div
                    style={{
                        position: 'absolute',
                        left: '50%',
                        bottom: screens?.xs ? 12 : 20,
                        transform: 'translateX(-50%)',
                        zIndex: 4,
                        padding: '8px 12px',
                        borderRadius: 16,
                        background: token.colorBgContainer,
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.16)',
                        pointerEvents: 'none'
                    }}
                >
                    <Spin size="small" style={{ color: loadingSpinnerColor }} />
                </div>
            )}
            <Image.PreviewGroup
                items={conversationImages}
                preview={{
                    visible: isImagePreviewOpen,
                    current: previewImageIndex,
                    onVisibleChange: setIsImagePreviewOpen,
                    onChange: setPreviewImageIndex,
                }}
            />
            {showJumpToLatest && (
                <Tooltip title={t('jump_to_latest_message')} placement="left">
                    <Button
                        aria-label={t('jump_to_latest_message')}
                        type="text"
                        shape="circle"
                        icon={<DownOutlined />}
                        onClick={jumpToLatest}
                        style={{
                            position: 'absolute',
                            bottom: (bottomInset || 0) + 12,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            zIndex: 2,
                            minWidth: 34,
                            width: 34,
                            height: 34,
                            padding: 0,
                            color: token.colorTextSecondary,
                            background: token.colorBgContainer,
                            border: 'none',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.16)'
                        }}
                    />
                </Tooltip>
            )}
        </div>
    );
});

const LoadingSpinner = ({ color }) => (
    <Row justify="center" style={{ padding: 20, order: 2 }}>
        <Spin style={{ color }} />
    </Row>
);

export default MessageList;
